import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { supabase } from '../../lib/supabase';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Textarea } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { FormRow } from '../../components/ui/FormRow';

type Proveedor = { id: string; nombre: string };
type Gama = { id: string; nombre: string; codigo: string };
type Categoria = { id: string; nombre: string };
type Subcategoria = { id: string; nombre: string; categoria_id: string };
type Detalle = { id: string; nombre: string; subcategoria_id: string; activo: boolean };

type StagedImagen = { preview: string; file: File };

type Form = {
  proveedor_id: string;
  ref_proveedor: string;
  nombre: string;
  main_specs: string;
  descripcion: string;
  moq: string;
  coste_usd: string;
  packaging_included: boolean;
  envio_tipo: 'FOB' | 'EXW';
  foc_pct: string;
  gama_id: string;
  categoria_id: string;
  subcategoria_id: string;
  detalle_id: string;
  // Datos logísticos
  units_40hq: string;
  unit_box_largo_cm: string;
  unit_box_ancho_cm: string;
  unit_box_alto_cm: string;
  ctn_largo_cm: string;
  ctn_ancho_cm: string;
  ctn_alto_cm: string;
  carton_box_pcs: string;
  // Imagen principal (data URL para preview, se sube tras crear)
  imagenPreview: string | null;
  imagenFile: File | null;
  // Imágenes adicionales (galería, se suben como imagen_adicional tras crear)
  imagenesAdicionales: StagedImagen[];
  // Ficha de producto (documento, se sube como 'ficha' tras crear)
  fichaFile: File | null;
};

const empty: Form = {
  proveedor_id: '',
  ref_proveedor: '',
  nombre: '',
  main_specs: '',
  descripcion: '',
  moq: '',
  coste_usd: '',
  packaging_included: true,
  envio_tipo: 'FOB',
  foc_pct: '',
  gama_id: '',
  categoria_id: '',
  subcategoria_id: '',
  detalle_id: '',
  units_40hq: '',
  unit_box_largo_cm: '',
  unit_box_ancho_cm: '',
  unit_box_alto_cm: '',
  ctn_largo_cm: '',
  ctn_ancho_cm: '',
  ctn_alto_cm: '',
  carton_box_pcs: '',
  imagenPreview: null,
  imagenFile: null,
  imagenesAdicionales: [],
  fichaFile: null,
};

const DOC_ACCEPT =
  '.pdf,.doc,.docx,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

type TipoArchivo = 'imagen_principal' | 'imagen_adicional' | 'ficha';

/** Sube un archivo al producto recién creado. Mismo endpoint que la ficha. */
async function subirArchivo(productoProveedorId: string, tipo: TipoArchivo, file: File): Promise<void> {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess.session?.access_token;
  const fd = new FormData();
  fd.append('tipo', tipo);
  fd.append('file', file);
  const res = await fetch(`/api/productos-proveedor/${productoProveedorId}/archivos`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: fd,
  });
  if (!res.ok) throw new Error(`Upload (${tipo}) falló: ${res.status}`);
}

export function ProductoProveedorNuevo() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const provedorIdParam = searchParams.get('proveedor') ?? '';

  const [form, setForm] = useState<Form>({ ...empty, proveedor_id: provedorIdParam });
  const dropRef = useRef<HTMLDivElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const adicionalesInputRef = useRef<HTMLInputElement | null>(null);
  const fichaInputRef = useRef<HTMLInputElement | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const proveedores = useQuery({
    queryKey: ['list', '/proveedores'],
    queryFn: () => api.get<{ data: Proveedor[] }>('/proveedores?limit=1000&activo=true'),
  });
  const gamas = useQuery({
    queryKey: ['list', '/admin/gamas'],
    queryFn: () => api.get<{ data: Gama[] }>('/admin/gamas'),
  });
  const categorias = useQuery({
    queryKey: ['list', '/admin/categorias'],
    queryFn: () => api.get<{ data: Categoria[] }>('/admin/categorias'),
  });
  const subcategorias = useQuery({
    queryKey: ['list', '/admin/subcategorias'],
    queryFn: () => api.get<{ data: Subcategoria[] }>('/admin/subcategorias'),
  });
  const detalles = useQuery({
    queryKey: ['list', '/admin/detalles'],
    queryFn: () => api.get<{ data: Detalle[] }>('/admin/detalles'),
  });

  const subcategoriasFiltradas = (subcategorias.data?.data ?? []).filter(
    (s) => !form.categoria_id || s.categoria_id === form.categoria_id,
  );
  const detallesFiltrados = (detalles.data?.data ?? []).filter(
    (d) => d.activo && (!form.subcategoria_id || d.subcategoria_id === form.subcategoria_id),
  );

  const upd = (patch: Partial<Form>) => setForm((f) => ({ ...f, ...patch }));

  // Main SPECS: al pegar texto en varias líneas, lo colapsamos a una sola
  // línea separando cada especificación con " / " y quitando los saltos.
  const onPasteMainSpecs = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const text = e.clipboardData.getData('text');
    if (!/[\r\n]/.test(text)) return; // una sola línea → pegado normal
    e.preventDefault();
    const joined = text
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean)
      .join(' / ');
    const t = e.currentTarget;
    const start = t.selectionStart ?? form.main_specs.length;
    const end = t.selectionEnd ?? form.main_specs.length;
    upd({ main_specs: form.main_specs.slice(0, start) + joined + form.main_specs.slice(end) });
  };

  // ── Imagen principal: paste global + drag&drop + file input ───────
  // El paste global va a la imagen principal SALVO que el foco esté en un
  // input/textarea o dentro de un slot que reclama el paste para sí
  // (data-paste-claimed), igual que en la ficha del producto de proveedor.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA)$/.test(target.tagName)) return;
      if (target && typeof target.closest === 'function' && target.closest('[data-paste-claimed="true"]')) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const it of items) {
        if (it.type.startsWith('image/')) {
          const file = it.getAsFile();
          if (file) handleImage(file);
          break;
        }
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []);

  const handleImage = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => upd({ imagenPreview: reader.result as string, imagenFile: file });
    reader.readAsDataURL(file);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) handleImage(file);
  };

  // ── Imágenes adicionales (galería) ───────────────────────────────
  const addImagenesAdicionales = (files: File[]) => {
    const imgs = files.filter((f) => f.type.startsWith('image/'));
    if (imgs.length === 0) return;
    imgs.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () =>
        setForm((f) => ({
          ...f,
          imagenesAdicionales: [...f.imagenesAdicionales, { preview: reader.result as string, file }],
        }));
      reader.readAsDataURL(file);
    });
  };
  const removeImagenAdicional = (idx: number) =>
    setForm((f) => ({ ...f, imagenesAdicionales: f.imagenesAdicionales.filter((_, i) => i !== idx) }));

  const filesFromPaste = (e: React.ClipboardEvent, onlyImages: boolean): File[] => {
    const items = e.clipboardData?.items;
    if (!items) return [];
    const out: File[] = [];
    for (const item of Array.from(items)) {
      if (item.kind !== 'file') continue;
      const blob = item.getAsFile();
      if (!blob) continue;
      if (onlyImages && !blob.type.startsWith('image/')) continue;
      const ext = (blob.type.split('/')[1] ?? 'bin').replace('+', '-');
      out.push(new File([blob], blob.name || `pegado-${ext}`, { type: blob.type || 'application/octet-stream' }));
    }
    return out;
  };

  // ── Crear producto ───────────────────────────────────────────────
  const create = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        proveedor_id: form.proveedor_id,
        ref_proveedor: form.ref_proveedor.trim(),
        nombre: form.nombre.trim(),
        main_specs: form.main_specs.trim() || null,
        descripcion: form.descripcion.trim() || null,
        moq: form.moq ? Number(form.moq) : null,
        coste_usd: form.coste_usd ? Number(form.coste_usd) : null,
        packaging_included: form.packaging_included,
        envio_tipo: form.envio_tipo,
        foc_pct: form.foc_pct ? Number(form.foc_pct) : null,
        gama_id: form.gama_id || null,
        categoria_id: form.categoria_id || null,
        subcategoria_id: form.subcategoria_id || null,
        detalle_id: form.detalle_id || null,
        units_40hq: form.units_40hq ? Number(form.units_40hq) : null,
        unit_box_largo_cm: form.unit_box_largo_cm ? Number(form.unit_box_largo_cm) : null,
        unit_box_ancho_cm: form.unit_box_ancho_cm ? Number(form.unit_box_ancho_cm) : null,
        unit_box_alto_cm: form.unit_box_alto_cm ? Number(form.unit_box_alto_cm) : null,
        ctn_largo_cm: form.ctn_largo_cm ? Number(form.ctn_largo_cm) : null,
        ctn_ancho_cm: form.ctn_ancho_cm ? Number(form.ctn_ancho_cm) : null,
        ctn_alto_cm: form.ctn_alto_cm ? Number(form.ctn_alto_cm) : null,
        carton_box_pcs: form.carton_box_pcs ? Number(form.carton_box_pcs) : null,
        origen: 'MANUAL',
      };
      const r = await api.post<{ id: string }>('/productos-proveedor', body);
      if (!r?.id) return r;

      // Histórico de precios: el coste + MOQ introducidos se registran como
      // la primera cotización (tramo 1) con la fecha de hoy. Así el producto
      // nace con su histórico de precios poblado.
      if (form.coste_usd) {
        try {
          await api.post(`/productos-proveedor/${r.id}/precios`, {
            fecha: new Date().toISOString().slice(0, 10),
            tramo1_unidades: form.moq ? Number(form.moq) : null,
            tramo1_precio_usd: Number(form.coste_usd),
            notas: 'Alta del producto',
          });
        } catch (err) {
          console.warn('Registrar precio inicial falló:', err);
        }
      }

      // Adjuntos: imagen principal, galería adicional y ficha de producto.
      // No bloquean la creación; si fallan se pueden subir desde la ficha.
      try {
        if (form.imagenFile) await subirArchivo(r.id, 'imagen_principal', form.imagenFile);
        for (const img of form.imagenesAdicionales) await subirArchivo(r.id, 'imagen_adicional', img.file);
        if (form.fichaFile) await subirArchivo(r.id, 'ficha', form.fichaFile);
      } catch (err) {
        console.warn('Subir adjuntos tras crear falló:', err);
      }
      return r;
    },
    onSuccess: (r) => {
      if (r?.id) navigate(`/proveedores/productos/${r.id}`);
    },
    onError: (err) => setErrorMsg((err as Error).message),
  });

  const submit = () => {
    if (!form.proveedor_id) return setErrorMsg('Selecciona un proveedor.');
    if (!form.ref_proveedor.trim()) return setErrorMsg('Falta Model/Ref.');
    if (!form.nombre.trim()) return setErrorMsg('Falta nombre.');
    setErrorMsg(null);
    create.mutate();
  };

  return (
    <div>
      <PageHeader
        title="Nuevo producto de proveedor"
        subtitle="Datos mínimos para crear la ficha. Tras guardar se abre la ficha completa."
        actions={
          <>
            <Button variant="secondary" onClick={() => navigate(-1)}>← Cancelar</Button>
            <Button variant="save" onClick={submit} disabled={create.isPending}>
              {create.isPending ? 'Guardando…' : 'Guardar y abrir ficha'}
            </Button>
          </>
        }
      />

      {errorMsg && (
        <div className="mb-4 rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {errorMsg}
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-12">
        <Card title="Datos principales" className="md:col-span-8">
          <div className="grid gap-3 sm:grid-cols-2">
            <FormRow label="Proveedor" required>
              <Select
                options={(proveedores.data?.data ?? []).map((p) => ({ value: p.id, label: p.nombre }))}
                value={form.proveedor_id}
                onChange={(e) => upd({ proveedor_id: e.target.value })}
                emptyLabel="— seleccionar —"
              />
            </FormRow>
            <FormRow label="Model / Ref." required>
              <Input value={form.ref_proveedor} onChange={(e) => upd({ ref_proveedor: e.target.value })} />
            </FormRow>
            <div className="sm:col-span-2">
              <FormRow label="Nombre" required>
                <Input value={form.nombre} onChange={(e) => upd({ nombre: e.target.value })} />
              </FormRow>
            </div>
            <div className="sm:col-span-2">
              <FormRow label="Main SPECS" hint="Al pegar varias líneas se unen en una sola separadas por “ / ”.">
                <Textarea
                  rows={3}
                  value={form.main_specs}
                  onChange={(e) => upd({ main_specs: e.target.value })}
                  onPaste={(e) => onPasteMainSpecs(e)}
                />
              </FormRow>
            </div>
            <div className="sm:col-span-2">
              <FormRow
                label="Descripción Completa - Ficha de producto"
                hint="Va a la descripción extendida del producto."
              >
                <Textarea rows={5} value={form.descripcion} onChange={(e) => upd({ descripcion: e.target.value })} />
              </FormRow>
            </div>
            <FormRow label="MOQ">
              <Input type="number" value={form.moq} onChange={(e) => upd({ moq: e.target.value })} />
            </FormRow>
            <FormRow label="Price (USD)" hint="Se registra como primer precio del histórico con la fecha de hoy.">
              <Input type="number" step="any" value={form.coste_usd} onChange={(e) => upd({ coste_usd: e.target.value })} />
            </FormRow>

            {/* Packaging included, FOB/EXW y F.O.C. se gestionan ahora en la
                COTIZACIÓN (la calculadora), no al dar de alta el producto de
                proveedor. Decisión CEO 2026-06-03. */}
            {/* Clasificación: Gama · Categoría · Subcategoría · Detalle en una
                sola fila y más estrechos. */}
            <div className="sm:col-span-2">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <FormRow label="Gama">
                  <Select
                    options={(gamas.data?.data ?? []).map((g) => ({ value: g.id, label: g.nombre }))}
                    value={form.gama_id}
                    onChange={(e) => upd({ gama_id: e.target.value })}
                  />
                </FormRow>
                <FormRow label="Categoría">
                  <Select
                    options={(categorias.data?.data ?? []).map((c) => ({ value: c.id, label: c.nombre }))}
                    value={form.categoria_id}
                    onChange={(e) =>
                      upd({ categoria_id: e.target.value, subcategoria_id: '', detalle_id: '' })
                    }
                  />
                </FormRow>
                <FormRow label="Subcategoría">
                  <Select
                    options={subcategoriasFiltradas.map((s) => ({ value: s.id, label: s.nombre }))}
                    value={form.subcategoria_id}
                    onChange={(e) => upd({ subcategoria_id: e.target.value, detalle_id: '' })}
                    disabled={!form.categoria_id}
                  />
                </FormRow>
                <FormRow label="Detalle">
                  <Select
                    options={detallesFiltrados.map((d) => ({ value: d.id, label: d.nombre }))}
                    value={form.detalle_id}
                    onChange={(e) => upd({ detalle_id: e.target.value })}
                    disabled={!form.subcategoria_id}
                  />
                </FormRow>
              </div>
            </div>
          </div>

          <h3 className="mt-6 mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Datos logísticos
          </h3>
          <div className="grid gap-3 sm:grid-cols-3">
            <FormRow label="Uds / contenedor 40HQ">
              <Input type="number" value={form.units_40hq} onChange={(e) => upd({ units_40hq: e.target.value })} />
            </FormRow>
            <FormRow label="Pcs / carton box">
              <Input type="number" value={form.carton_box_pcs} onChange={(e) => upd({ carton_box_pcs: e.target.value })} />
            </FormRow>
            <FormRow label="Caja largo (cm)">
              <Input type="number" step="any" value={form.unit_box_largo_cm} onChange={(e) => upd({ unit_box_largo_cm: e.target.value })} />
            </FormRow>
            <FormRow label="Caja ancho (cm)">
              <Input type="number" step="any" value={form.unit_box_ancho_cm} onChange={(e) => upd({ unit_box_ancho_cm: e.target.value })} />
            </FormRow>
            <FormRow label="Caja alto (cm)">
              <Input type="number" step="any" value={form.unit_box_alto_cm} onChange={(e) => upd({ unit_box_alto_cm: e.target.value })} />
            </FormRow>
            <FormRow label="CTN largo (cm)" hint="Master carton">
              <Input type="number" step="any" value={form.ctn_largo_cm} onChange={(e) => upd({ ctn_largo_cm: e.target.value })} />
            </FormRow>
            <FormRow label="CTN ancho (cm)">
              <Input type="number" step="any" value={form.ctn_ancho_cm} onChange={(e) => upd({ ctn_ancho_cm: e.target.value })} />
            </FormRow>
            <FormRow label="CTN alto (cm)">
              <Input type="number" step="any" value={form.ctn_alto_cm} onChange={(e) => upd({ ctn_alto_cm: e.target.value })} />
            </FormRow>
          </div>
        </Card>

        <div className="flex flex-col gap-6 md:col-span-4">
          <Card title="Imagen principal" description="Pega (Ctrl+V), arrastra o pulsa para subir.">
            <div
              ref={dropRef}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
              className={`flex h-64 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed text-center transition ${
                form.imagenPreview ? 'border-slate-200 bg-white' : 'border-slate-300 bg-slate-50 hover:bg-slate-100'
              }`}
            >
              {form.imagenPreview ? (
                <img src={form.imagenPreview} alt="Preview" className="max-h-full max-w-full object-contain" />
              ) : (
                <div className="px-4 text-sm text-slate-500">
                  <div className="text-3xl">📷</div>
                  <div className="mt-1">Arrastra, pega Ctrl+V o haz click</div>
                </div>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleImage(file);
                if (fileRef.current) fileRef.current.value = '';
              }}
            />
            {form.imagenPreview && (
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); upd({ imagenPreview: null, imagenFile: null }); }}
                  className="cursor-pointer text-xs text-rose-600 hover:underline"
                >
                  Quitar imagen
                </button>
              </div>
            )}
          </Card>

          <Card
            title={`Imágenes adicionales (${form.imagenesAdicionales.length})`}
            description="Se añadirán como imágenes secundarias. Arrastra, pega con Ctrl+V o pulsa para subir."
          >
            <div
              data-paste-claimed="true"
              tabIndex={0}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                addImagenesAdicionales(Array.from(e.dataTransfer.files ?? []));
              }}
              onPaste={(e) => {
                const files = filesFromPaste(e, true);
                if (files.length === 0) return;
                e.preventDefault();
                e.stopPropagation();
                addImagenesAdicionales(files);
              }}
              onClick={() => adicionalesInputRef.current?.click()}
              className="flex min-h-24 cursor-pointer flex-wrap gap-2 rounded-md border-2 border-dashed border-slate-300 bg-slate-50 p-2 outline-none transition hover:bg-slate-100 focus:border-sky-500 focus:bg-sky-50"
              title="Click + Ctrl+V, arrastra o click para elegir archivos"
            >
              {form.imagenesAdicionales.length === 0 ? (
                <div className="flex w-full items-center justify-center px-2 py-4 text-center text-xs text-slate-500">
                  Arrastra, pega Ctrl+V o haz click para añadir varias imágenes
                </div>
              ) : (
                form.imagenesAdicionales.map((img, idx) => (
                  <div key={idx} className="group relative">
                    <div className="h-20 w-20 overflow-hidden rounded-md border border-slate-200 bg-white">
                      <img src={img.preview} alt={`Adicional ${idx + 1}`} className="h-full w-full object-contain" />
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeImagenAdicional(idx); }}
                      className="absolute -right-1 -top-1 hidden h-5 w-5 cursor-pointer items-center justify-center rounded-full bg-rose-600 text-xs font-bold text-white hover:bg-rose-700 group-hover:flex"
                    >×</button>
                  </div>
                ))
              )}
            </div>
            <input
              ref={adicionalesInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                const fs = e.target.files;
                if (fs && fs.length > 0) addImagenesAdicionales(Array.from(fs));
                if (adicionalesInputRef.current) adicionalesInputRef.current.value = '';
              }}
            />
          </Card>

          <Card
            title="Ficha de producto"
            description="PDF, DOC o XLS. Arrastra el archivo, pega con Ctrl+V o pulsa para subir."
          >
            <div
              data-paste-claimed="true"
              tabIndex={0}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files?.[0];
                if (f) upd({ fichaFile: f });
              }}
              onPaste={(e) => {
                const files = filesFromPaste(e, false);
                if (files.length === 0) return;
                e.preventDefault();
                e.stopPropagation();
                upd({ fichaFile: files[0] });
              }}
              onClick={() => fichaInputRef.current?.click()}
              className="flex min-h-16 cursor-pointer items-center justify-center rounded-md border-2 border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-center text-sm text-slate-500 outline-none transition hover:bg-slate-100 focus:border-sky-500 focus:bg-sky-50"
              title="Click + Ctrl+V, arrastra o click para elegir el archivo"
            >
              {form.fichaFile ? (
                <span className="font-medium text-slate-700">📄 {form.fichaFile.name}</span>
              ) : (
                '+ Ficha de producto · arrastra, Ctrl+V o click'
              )}
            </div>
            <input
              ref={fichaInputRef}
              type="file"
              accept={DOC_ACCEPT}
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) upd({ fichaFile: f });
                if (fichaInputRef.current) fichaInputRef.current.value = '';
              }}
            />
            {form.fichaFile && (
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); upd({ fichaFile: null }); }}
                  className="cursor-pointer text-xs text-rose-600 hover:underline"
                >
                  Quitar ficha
                </button>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
